import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { TierBox, LeftText, RightText } from "src/scenes/ClientLicense/components/LicenseTiersModal";
import CheckIcon from "src/img/Green-Check.svg";
import { Toast } from "src/components";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import { UserKeys } from "src/constants/userDetails";
import { RadioCircle } from "src/components/Documents/DocumentUploader.style";

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
`;

const Modal = styled.div`
    width: 674px;
    max-height: 589px;
    background: #fff;
    border: 1px solid #D8DEEC;
    box-shadow: 0px 5px 5px #00000014;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const Header = styled.div`
    padding: 50px 50px 0 50px;
`;

const Title = styled.div`
    font: bold 40px/24px Manrope;
    color: #11141A;
    margin-bottom: 20px;
`;

const Description = styled.div`
    font: 14px/16px Manrope;
    color: #000;
    margin-top: 10px;
`;

const Hr = styled.div`
    height: 1px;
    background: #ddd;
    margin: 20px 0;
    width: calc(100% + 100px);
    margin-left: -50px;
`;

const Content = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 0 50px;
`;

const FooterContainer = styled.div`
    padding: 0 50px 30px 50px;
    margin-top: auto;
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const SubmitButton = styled.button`
    background: ${p => p.disabled ? "#B9B9B9" : "#171A22"};
    color: #fff;
    border: none;
    border-radius: 7px;
    padding: 10px 18px;
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    width: 160px;
    height: 40px;
`;
export const LICENSE_PAYMENT_SUCCESS = "LICENSE_PAYMENT_SUCCESS";

export default function TierSelectionModal({
    open,
    onClose,
    tiers,
    renewalPeriod,
    shortTermDuration,
    onSelect,
    existingTier,
    renewalCurrency
}) {
    if (!open) return null;

    const requiresPayment = shortTermDuration === 0;
    const token = localStorage.getItem(UserKeys.TOKEN);

    const [selectedTier, setSelectedTier] = useState(null);
    const [loadingPay, setLoadingPay] = useState(false);
    const [confirmed, setConfirmed] = useState(false);

    useEffect(() => {
        setSelectedTier(existingTier || null);
        setConfirmed(!!existingTier); 
    }, [existingTier, open]);

    const effectiveTier = selectedTier;
    const displayTiers =
        existingTier ? [existingTier] : tiers;

    const handleConfirmSelect = async () => {
        if (!effectiveTier) return;

        try {
            await onSelect(effectiveTier.uuid);
            setConfirmed(true);
            if (!existingTier && requiresPayment) {
                await handleProceedToPay();
                return;
            } else {
                Toast.success("Licence activated successfully");

                setTimeout(() => {
                    window.location.reload();
                }, 1200);
            }

        } catch (err) {
            console.error(err);
            Toast.error("Failed to proceed");
        }
    };

    const handleProceedToPay = async () => {
        try {
            setLoadingPay(true);
            
            const res = await axios.get(`${API_URL}/getRenewalLink`, {
                headers: {
                    Authorization: "Bearer " + token
                }
            });

            const link = res.data?.renewalLink;
            if (!link) throw new Error("Payment link not ready yet");

            window.open(link, "_blank");

        } catch (err) {
            console.error(err);
            Toast.error("Payment system not ready yet. Please try again.");
        } finally {
            setLoadingPay(false);
        }
    };

    // Handle auto reload after successful payment in the new tab
    useEffect(() => {
        const handler = (event) => {
            if (event.origin !== window.location.origin) return;

            if (event.data?.type === LICENSE_PAYMENT_SUCCESS) {
                Toast.success("Payment successful!");
                window.location.reload();
            }
        };

        window.addEventListener("message", handler);

        return () => window.removeEventListener("message", handler);
    }, []);

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>

                <Header>
                    <Title>Choose Your Licence</Title>
                    <Description>
                        Select the licence that best suits your access needs.
                    </Description>
                    <Hr />
                </Header>

                <Content>
                    {displayTiers.map((tier) => {
                        const isSelected = effectiveTier?.uuid === tier.uuid;

                        return (
                            <TierBox
                                key={tier.uuid}
                                onClick={() => setSelectedTier(tier)}
                                style={{
                                    background: isSelected ? "rgba(91,110,245,0.12)" : "#fff",
                                    border: "1px solid #D8DEEC",
                                    borderRadius: "15px",
                                    padding: "12px",
                                    marginBottom: "10px",
                                    cursor: "pointer"
                                }}
                            >
                                <LeftText>
                                    <div style={{ fontWeight: 700 }}>
                                        {tier.tierName} License
                                    </div>
                                    <div style={{ fontSize: "13px", color: "#555" }}>
                                        {renewalCurrency} ${tier.renewalCost} ({renewalPeriod})
                                    </div>
                                </LeftText>

                                <RightText>
                                    {isSelected ? (
                                        <img src={CheckIcon} width="26" height="26" />
                                    ) : (
                                        <RadioCircle />
                                    )}
                                </RightText>
                            </TierBox>
                        );
                    })}
                </Content>

                <FooterContainer>
                    <Hr />

                    <Footer>

                        {/* KEY LOGIC */}
                        {requiresPayment ? (
                            confirmed ? (
                                <SubmitButton
                                    onClick={handleProceedToPay}
                                    disabled={loadingPay || !effectiveTier}
                                >
                                    {loadingPay ? "Loading..." : "Proceed to Pay"}
                                </SubmitButton>
                            ) : (
                                <SubmitButton
                                    onClick={handleConfirmSelect}
                                    disabled={!effectiveTier}
                                >
                                    Confirm & Pay
                                </SubmitButton>
                            )
                        ) : (
                            <SubmitButton
                                onClick={handleConfirmSelect}
                                disabled={!effectiveTier}
                            >
                                Confirm
                            </SubmitButton>
                        )}

                    </Footer>
                </FooterContainer>

            </Modal>
        </Overlay>
    );
}